import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function LiverpoolPage() {
  return (
    <>
      <LocalBusinessSchema city="Liverpool" latitude={53.4084} longitude={-2.9916} />
      <PageHero
        title="Fractional CISO Services in Liverpool"
        subtitle="Cybersecurity leadership for the Liverpool City Region's digital growth"
        eyebrow="Liverpool"
        seoTitle="Fractional CISO Liverpool | The CISO Network"
        seoDescription="Expert fractional CISO services for Liverpool businesses. Access senior cybersecurity leadership for the city region's tech and health sectors."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Securing the Liverpool City Region's Digital Future
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Liverpool is undergoing a remarkable digital transformation, with the city region
                  emerging as a significant player in health technology, creative digital, and
                  professional services. The Knowledge Quarter and Baltic Triangle have become
                  vibrant hubs for innovation, attracting investment and talent to this historic
                  city.
                </p>
                <p>
                  Liverpool businesses face evolving cybersecurity challenges. The city's strength
                  in health and life sciences means many firms handle sensitive NHS and patient data
                  requiring rigorous protection. Creative digital agencies work with high-profile
                  clients and valuable intellectual property. Meanwhile, the growing professional
                  services sector must maintain client confidentiality and meet regulatory
                  standards.
                </p>
                <p>
                  Many Liverpool businesses are ambitious scale-ups or established SMEs serving
                  national clients. These organisations need demonstrable security leadership to win
                  contracts and build trust, but the local market for full-time CISOs is limited.
                  Our fractional CISO service provides Liverpool businesses with accessible,
                  locally-aware security expertise that helps them compete beyond the city region.
                </p>
                <p>
                  Liverpool's economy has transformed significantly, with the Baltic Triangle and
                  waterfront areas now hosting digital, creative, and health technology businesses
                  alongside the city's traditional maritime and public sector strengths. These
                  diverse sectors each carry distinct security requirements — from port
                  cybersecurity to patient data protection. A fractional CISO with broad sector
                  experience can navigate this complexity.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique dynamics of the Liverpool City
                      Region business community.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      City Region Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across Liverpool, Wirral, Knowsley, and the wider city
                      region.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Liverpool Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Accessible security leadership for the Liverpool City Region
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Health Data Expertise',
                description:
                  'Navigate NHS Digital and healthcare data protection requirements with confidence.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description: 'Access senior CISO expertise without the full-time executive salary.',
              },
              {
                icon: Users,
                title: 'Local Understanding',
                description:
                  "CISOs who appreciate Liverpool's unique business culture and ambitions.",
              },
              {
                icon: Clock,
                title: 'Flexible Support',
                description:
                  'Scale security leadership as your Liverpool business grows its reach.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Liverpool - FAQs"
        subtitle="Common questions from Liverpool and Merseyside businesses"
        items={[
          {
            id: 'liverpool-1',
            question: 'How does a fractional CISO work with businesses in Liverpool?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Liverpool. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing maritime, health, and digital creative companies.',
          },
          {
            id: 'liverpool-2',
            question: 'What size of business benefits from a fractional CISO in Liverpool?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Liverpool startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'liverpool-3',
            question: 'What industries do your CISOs cover in Liverpool?',
            answer:
              "Our network includes CISOs with experience in maritime, health, and digital creative. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'liverpool-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'liverpool-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Liverpool Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your Liverpool
            business.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
